// Payment Enums
export enum PaymentCurrency {
  RUB = 'RUB',
  EUR = 'EUR',
  USD = 'USD',
  UAH = 'UAH',
  BYN = 'BYN'
}

// Публичные коды способов оплаты — то, что уходит в POST /payments и попадает в DOM.
// Имён платёжных провайдеров тут нет и быть не должно: требование провайдера — ни на
// странице, ни в коде элемента, ни в бандле. Кто проводит платёж, решает бэк у себя.
// Границы правила — docs/ui-patterns.md §8.
export enum PaymentGateway {
  RUB_SBP = 'RUB_SBP',
  RUB_SBP_ALT = 'RUB_SBP_ALT',
  RUB_CARD = 'RUB_CARD',
  RUB_CARD_ALT = 'RUB_CARD_ALT',
  INTL_CARD = 'INTL_CARD',
  BYN_ERIP = 'BYN_ERIP',
  CRYPTO = 'CRYPTO',
  CRYPTO_ALT = 'CRYPTO_ALT',
  UAH_P2P = 'UAH_P2P',
  UAH_P2P_CARD = 'UAH_P2P_CARD',
  UAH_QR = 'UAH_QR'
}

export enum PaymentStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED'
}

// Лимиты суммы оплаты по гейтвею (в единицах валюты, НЕ монет)
export const GATEWAY_AMOUNT_LIMITS: Record<PaymentGateway, { min: number; max: number }> = {
  [PaymentGateway.RUB_SBP]: { min: 10, max: 19000 },
  [PaymentGateway.RUB_SBP_ALT]: { min: 10, max: 100000 },
  [PaymentGateway.RUB_CARD]: { min: 10, max: 28999 },
  [PaymentGateway.RUB_CARD_ALT]: { min: 100, max: 19000 },
  [PaymentGateway.INTL_CARD]: { min: 5, max: 1000 },
  [PaymentGateway.BYN_ERIP]: { min: 10, max: 4000 },
  [PaymentGateway.CRYPTO]: { min: 10, max: 1000 },
  [PaymentGateway.CRYPTO_ALT]: { min: 5, max: 5000 },
  [PaymentGateway.UAH_P2P]: { min: 300, max: 30000 },
  [PaymentGateway.UAH_P2P_CARD]: { min: 1000, max: 30000 },
  [PaymentGateway.UAH_QR]: { min: 1000, max: 50000 },
};

// Payment DTOs
export interface CreatePaymentDto {
  coinAmount: number;
  gateway: string;
}

export interface PaymentRequestDto {
  id: number;
  coinAmount: number;
  amount: number;
  currency: PaymentCurrency;
  status: PaymentStatus;
  paymentSystemId: string;
  paymentUrl?: string;
  createdAt: string;
  completedAt?: string;
}

export interface PaginatedPaymentRequestDto {
  content: PaymentRequestDto[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
}

// Donation Bonus Types
export interface DonationBonusItemInfoDto {
  id: number;
  icon: string;
  iconPanel: string;
  name: string;
  wikiCategoryId?: number | null;
  wikiSubCategoryId?: number | null;
  wikiTypeId?: number | null;
  wikiSubTypeId?: number | null;
}

export interface DonationBonusItemDto {
  id: number;
  baseItem: DonationBonusItemInfoDto;
  quantity: number;
}

export interface DonationBonusDto {
  id: number;
  minAmount: number;
  maxAmount: number;
  items: DonationBonusItemDto[];
  createdAt: string;
}

export interface CreateDonationBonusDto {
  minAmount: number;
  maxAmount: number;
  items: CreateDonationBonusItemDto[];
}

export interface CreateDonationBonusItemDto {
  baseItemId: number;
  quantity: number;
}

export interface UpdateDonationBonusDto {
  minAmount: number;
  maxAmount: number;
  items: UpdateDonationBonusItemDto[];
}

export interface UpdateDonationBonusItemDto {
  baseItemId: number;
  quantity: number;
}

export interface PaginatedDonationBonusDto {
  content: DonationBonusDto[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
}

// Donation Coin Bonus Types (Percentage bonuses)
export interface DonationCoinBonusDto {
  id: number;
  minAmount: number;
  maxAmount: number;
  bonusPercent: number;
  createdAt: string;
}

export interface CreateDonationCoinBonusDto {
  minAmount: number;
  maxAmount: number;
  bonusPercent: number;
}

export interface UpdateDonationCoinBonusDto {
  minAmount: number;
  maxAmount: number;
  bonusPercent: number;
}

export interface PaginatedDonationCoinBonusDto {
  content: DonationCoinBonusDto[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
}

// Admin Payment Types
export interface PaymentUserCurrencyStatsDto {
  currency: string;
  totalAmount: number;
  totalCoinAmount: number;
  count: number;
}

export interface PaymentUserDto {
  userId: number;
  username: string;
  email: string;
  completed: PaymentUserCurrencyStatsDto[];
  pending: PaymentUserCurrencyStatsDto[];
}

export interface PaginatedPaymentUsersDto {
  content: PaymentUserDto[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
}

export interface CurrencyStatsDto {
  currency: string;
  totalAmount: number;
  totalCount: number;
}

export interface PaymentSummaryDto {
  totalCoinAmount: number;
  totalBonusCoinAmount: number;
  totalCount: number;
  averageCoinAmountPerUser: number;
  currencyStats: CurrencyStatsDto[];
}

export interface PaymentGatewayStatsDto {
  gateway: string;
  currency: string;
  totalAmount: number;
  totalCoinAmount: number;
  totalCount: number;
}

export interface AdminPaymentRecordDto {
  id: number;
  userId: number;
  username: string;
  email: string;
  coinAmount: number;
  bonusCoinAmount: number;
  amount: number;
  currency: string;
  gateway: string;
  status: string;
  paymentSystemId: string;
  clanId?: number;
  /** Имя клана, сохранённое в платеже (переживает вайп); null у старых платежей */
  clanName?: string | null;
  createdAt: string;
  completedAt?: string;
  /** Тестовый платёж: исключён из всей статистики платежей (PATCH /admin/payments/{id}/test) */
  test: boolean;
}

/** Фильтр testFilter экранов платежей: REAL (по умолчанию бэка) — только реальные, TEST — только тестовые, ALL — все */
export type PaymentTestFilter = 'REAL' | 'TEST' | 'ALL';
export const PAYMENT_TEST_FILTERS: PaymentTestFilter[] = ['REAL', 'TEST', 'ALL'];
/** Значение из URL (?testFilter=) или REAL, если пусто/мусор */
export const parsePaymentTestFilter = (v: string | null): PaymentTestFilter =>
  PAYMENT_TEST_FILTERS.includes(v as PaymentTestFilter) ? (v as PaymentTestFilter) : 'REAL';

export interface PaginatedAdminPaymentsDto {
  content: AdminPaymentRecordDto[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
}

// Clan Donation Types (Admin Payments)
export interface ClanDonationSummaryDto {
  clanId: number;
  /** Имя из платежа, а не из игровой БД; null у старых платежей */
  clanName: string | null;
  totalCoinAmount: number;
  totalBonusCoinAmount: number;
  totalCount: number;
}

export interface ClanDonationRecordDto {
  id: number;
  userId: number;
  username: string;
  coinAmount: number;
  bonusCoinAmount: number;
  amount: number;
  currency: string;
  gateway: string;
  completedAt: string;
}

export interface PaginatedClanDonationsDto {
  content: ClanDonationRecordDto[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
}

// Top Donators Types (Admin)
export interface TopDonatorDto {
  userId: number;
  username: string;
  email: string;
  totalCoinAmount: number;
  totalBonusCoinAmount: number;
  totalCount: number;
}

export interface PaginatedTopDonatorsDto {
  content: TopDonatorDto[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
}

// Shop Stats Types (Admin)
export interface ShopItemStatsDto {
  shopItemId: number;
  itemName: string;
  icon: string;
  iconPanel: string;
  totalQuantity: number;
  totalRevenue: number;
  purchaseCount: number;
  wikiItemLevel?: number;
}

export interface PaginatedShopItemStatsDto {
  content: ShopItemStatsDto[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
}

export interface ShopPurchaseRecordDto {
  id: number;
  userId: number;
  username: string;
  itemName: string;
  icon: string;
  iconPanel: string;
  quantity: number;
  totalPrice: number;
  wikiItemLevel?: number;
  purchasedAt: string;
}

export interface PaginatedShopPurchasesDto {
  content: ShopPurchaseRecordDto[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
}

// Roulette Stats Types
export interface RouletteSpinStatsDto {
  totalSpins: number;
  totalCoinsSpent: number;
  totalTokensUsed: number;
  totalFreeSpins: number;
  totalPaidSpins: number;
}

export interface RouletteItemStatsDto {
  itemId: number;
  itemName: string;
  iconName: string;
  iconPanel: string;
  timesWon: number;
  totalQuantityWon: number;
}

export interface RouletteItemStatsResponseDto {
  mainItems: RouletteItemStatsDto[];
  bonusItems: RouletteItemStatsDto[];
}

export interface RouletteSpinHistoryDto {
  id: number;
  userId: number;
  username: string;
  wonItemName: string;
  wonItemIcon: string;
  wonItemIconPanel: string;
  wonItemCount: number;
  bonusItemName?: string;
  bonusItemIcon?: string;
  bonusItemIconPanel?: string;
  bonusItemCount?: number;
  tokenUsed: boolean;
  freeSpin: boolean;
  cost: number;
  createdAt: string;
}

export interface PaginatedRouletteSpinHistoryDto {
  content: RouletteSpinHistoryDto[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
}

// Roulette User Stats Types
export interface RouletteUserStatsDto {
  userId: number;
  username: string;
  totalSpins: number;
  totalCoinsSpent: number;
  totalTokensUsed: number;
  totalFreeSpins: number;
  totalPaidSpins: number;
}

export interface PaginatedRouletteUserStatsDto {
  content: RouletteUserStatsDto[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
}
